/**
 * ProblemCard — anxiety-naming panel that sits beside the hero headline.
 *
 * Fixed-dark treatment regardless of site light/dark mode (a deliberate
 * "spotlight" surface, not a theme-following one — see homepage redesign
 * plan Task 3). No decorative illustration per DESIGN_BRIEF's ban on
 * anxiety-amplifying imagery; impact comes from typography + the sourced
 * stat instead.
 *
 * Coral accent: the theme's palette does not expose a raw `coral` shade
 * scale (only `product`, `coralFill`, `tint`, `identity` — see
 * theme/index.ts), so `coral[200]` is imported directly from
 * theme/tokens and used as a literal color value. This is the only
 * consumer of this shade; not worth extending the shared theme for.
 */

import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { coral, inverseInk, inversePaper } from "../../theme/tokens";

const POINT_KEYS = [
  { labelKey: "ProblemCard.point1Label", bodyKey: "ProblemCard.point1Body" },
  { labelKey: "ProblemCard.point2Label", bodyKey: "ProblemCard.point2Body" },
  { labelKey: "ProblemCard.point3Label", bodyKey: "ProblemCard.point3Body" },
  { labelKey: "ProblemCard.point4Label", bodyKey: "ProblemCard.point4Body" },
];

export default function ProblemCard() {
  const { t } = useTranslation("home");
  return (
    <Box
      sx={{
        p: { xs: 3, md: 3.5 },
        borderRadius: "16px",
        bgcolor: inverseInk,
        color: inversePaper,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: coral[200],
          }}
        />
        <Typography
          component="span"
          sx={{ fontSize: 14, fontWeight: 500, color: coral[200] }}
        >
          {t("ProblemCard.eyebrow")}
        </Typography>
      </Stack>

      <Typography
        component="h2"
        sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 500, lineHeight: 1.22, m: 0 }}
      >
        {t("ProblemCard.heading")}
      </Typography>

      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: "8px",
          bgcolor: "rgba(227,93,106,0.16)",
          borderLeft: "3px solid",
          borderLeftColor: coral[200],
          fontStyle: "italic",
          fontSize: 15,
        }}
      >
        {t("ProblemCard.quote")}
      </Box>

      <Stack
        direction="row"
        spacing={1.5}
        alignItems="baseline"
        sx={{
          mt: 2,
          p: 2,
          borderRadius: "8px",
          bgcolor: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <Typography
          component="span"
          sx={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace', fontSize: 28, fontWeight: 500, color: coral[200], flexShrink: 0 }}
        >
          {t("ProblemCard.statNumber")}
        </Typography>
        <Typography component="span" sx={{ fontSize: 14 }}>
          {t("ProblemCard.statBody")}
        </Typography>
      </Stack>

      <Stack spacing={0} sx={{ mt: 2.5 }}>
        {POINT_KEYS.map((p, i) => (
          <Stack
            key={p.labelKey}
            direction="row"
            spacing={1.5}
            sx={{
              pt: i === 0 ? 0 : 1.5,
              mt: i === 0 ? 0 : 1.5,
              borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: coral[200],
                flexShrink: 0,
                mt: 0.8,
              }}
            />
            <Typography sx={{ fontSize: 14.5 }}>
              <Box component="span" sx={{ fontWeight: 500 }}>
                {t(p.labelKey)}
              </Box>{" "}
              {t(p.bodyKey)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
